import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,Alert
} from '@mui/material';
import axios from 'axios';
import { History, Visibility, Person, Work, Psychology, Feedback } from '@mui/icons-material';

function SuperAdminInterviews() {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    const fetchInterviews = async () => {
      try {
        const response = await axios.get('/api/super-admin/interviews');
        setInterviews(response.data);
      } catch (error) {
        console.error('Error fetching interviews:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInterviews();
  }, []);

  const handleOpenReport = (interview) => {
    setSelectedInterview(interview);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedInterview(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      in_progress: 'info',
      shortlisted: 'success',
      rejected: 'error',
      offered: 'success',
    };
    return colors[status] || 'default';
  };

  const renderAssessmentSummary = (title, assessment, icon) => {
    if (!assessment) return null;

    const feedback = assessment.feedback || assessment.notes;
    const score = assessment.score;
    const maxScore = assessment.max_score || assessment.maxScore || 100;

    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {icon}
          <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 'bold' }}>
            {title}
          </Typography>
          {score !== undefined && score !== null && (
            <Chip 
              label={`Score: ${score}/${maxScore}`} 
              size="small" 
              color={score >= (maxScore * 0.7) ? 'success' : (score >= (maxScore * 0.4) ? 'warning' : 'error')}
              sx={{ ml: 2 }}
            />
          )}
        </Box>
        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fcfcfc' }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            AI Feedback / Notes:
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {feedback || 'No feedback available.'}
          </Typography>
        </Paper>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4"
                  sx={{
                    color: "#4F46E5",
                    fontWeight: "bold",
                    mb: 2
                  }}
      >
        All Interviews History
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {interviews.map((app) => (
          <Grid item xs={12} md={6} lg={4} key={app.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {app.candidate?.firstName} {app.candidate?.lastName}
                  </Typography>
                  <Chip
                    label={app.status}
                    color={getStatusColor(app.status)}
                    size="small"
                  />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary' }}>
                  <Person sx={{ fontSize: 20, mr: 1 }} />
                  <Typography variant="body2">{app.candidate?.email}</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary' }}>
                  <Work sx={{ fontSize: 20, mr: 1 }} />
                  <Typography variant="body2">{app.job?.title || 'Unknown Job'}</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary' }}>
                  <History sx={{ fontSize: 20, mr: 1 }} />
                  <Typography variant="body2">Applied: {new Date(app.created_at || app.createdAt).toLocaleDateString()}</Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {app.codingTest && <Chip icon={<Psychology />} label="Coding" size="small" variant="outlined" color="primary" />}
                  {app.technicalInterview && <Chip icon={<Psychology />} label="Tech" size="small" variant="outlined" color="primary" />}
                  {app.hrInterview && <Chip icon={<Psychology />} label="HR" size="small" variant="outlined" color="primary" />}
                </Box>
              </CardContent>
              <Box sx={{ p: 2, pt: 0 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Visibility />}
                  onClick={() => handleOpenReport(app)}
                >
                  View AI Report
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
        {interviews.length === 0 && (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography color="text.secondary">No interview records found.</Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* AI Report Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          AI Feedback Report - {selectedInterview?.candidate?.firstName} {selectedInterview?.candidate?.lastName}
        </DialogTitle>
        <DialogContent dividers>
          {selectedInterview && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Application Details
              </Typography>
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Position:</Typography>
                  <Typography variant="body1" fontWeight="bold">{selectedInterview.job?.title}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Company:</Typography>
                  <Typography variant="body1">{selectedInterview.job?.company_name || selectedInterview.job?.companyName}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Application Date:</Typography>
                  <Typography variant="body1">{new Date(selectedInterview.created_at || selectedInterview.createdAt).toLocaleDateString()}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Current Status:</Typography>
                  <Chip label={selectedInterview.status} color={getStatusColor(selectedInterview.status)} size="small" />
                </Grid>
              </Grid>

              <Divider sx={{ mb: 4 }} />

              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <Feedback sx={{ mr: 1 }} /> AI Assessments Feedback
              </Typography>

              {renderAssessmentSummary('Coding Test', selectedInterview.codingTest, <Psychology />)}
              {renderAssessmentSummary('Technical Interview', selectedInterview.technicalInterview, <Psychology />)}
              {renderAssessmentSummary('HR Interview', selectedInterview.hrInterview, <Psychology />)}

              {!(selectedInterview.codingTest || selectedInterview.technicalInterview || selectedInterview.hrInterview) && (
                <Alert severity="info">No assessments have been completed yet for this candidate.</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default SuperAdminInterviews;
